import { FC, useState } from 'react';
import dayjs from 'dayjs';

import { Drawer, MenuItem, Stack, TextField } from '@mui/material';

import { CreateMcpApiKeyRequest } from '@/apis/admin-api';
import { PrimaryButton, SecondaryButton } from '@/components/Shared/Buttons';

import { DEFAULT_EXPIRY_DAYS, EXPIRY_OPTIONS, fieldSx } from '../constants';
import { ScopeChips } from './KeysGrid';
import { ServerRow } from './ServersGrid';

import styles from '../styles.module.scss';

type CreateKeySidepanelProps = {
  servers: ServerRow[];
  initialServerId: string;
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: (request: CreateMcpApiKeyRequest) => void;
};

const MAX_NAME_LENGTH = 128;

export const CreateKeySidepanel: FC<CreateKeySidepanelProps> = ({
  servers,
  initialServerId,
  isSubmitting,
  onCancel,
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [expiresInDays, setExpiresInDays] = useState(DEFAULT_EXPIRY_DAYS);
  const [serverId, setServerId] = useState(initialServerId ?? servers[0]?.id);
  const server = servers.find(({ id }) => id === serverId);

  const handleSubmit = () => {
    if (isSubmitting || !server) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }
    if (trimmedName.length > MAX_NAME_LENGTH) {
      setError(`Name must be at most ${MAX_NAME_LENGTH} characters`);
      return;
    }
    onSubmit({
      name: trimmedName,
      expiresInDays,
      ...(server.isBuiltIn ? { builtInServer: server.name } : { endpointId: server.id }),
    });
  };

  return (
    <Drawer open anchor="right" onClose={onCancel}>
      <div className={styles['sidepanel']}>
        <Stack flex="auto 1 1" spacing="20px" overflow="auto">
          <p style={{ fontWeight: '600', margin: '8px 0 12px' }}>New MCP API key</p>
          <TextField
            required
            label="Name"
            placeholder="e.g. Claude Code on work laptop"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(undefined);
            }}
            error={!!error}
            helperText={error}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
            }}
            sx={fieldSx}
          />
          <TextField
            select
            label="Server"
            value={server?.id ?? ''}
            onChange={(e) => setServerId(e.target.value)}
            error={!server}
            helperText={server ? server.url : 'This server no longer exists. Pick another server.'}
            sx={fieldSx}>
            {servers.map(({ id, name: serverName, isBuiltIn }) => (
              <MenuItem key={id} value={id} sx={{ fontSize: 14 }}>
                {isBuiltIn ? `${serverName} (built-in)` : serverName}
              </MenuItem>
            ))}
          </TextField>
          <div>
            <p className={styles['muted']}>The key only works on this server and gets its access:</p>
            <ScopeChips scopes={server?.scopes ?? []} />
          </div>
          <TextField
            select
            label="Expires"
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(Number(e.target.value))}
            helperText={`Expires on ${dayjs().add(expiresInDays, 'day').format('DD/MM/YYYY')}`}
            sx={fieldSx}>
            {EXPIRY_OPTIONS.map((days) => (
              <MenuItem key={days} value={days} sx={{ fontSize: 14 }}>
                {days === 365 ? '1 year' : `${days} days`}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <div style={{ height: 40, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SecondaryButton width={150} label="Cancel" onClick={onCancel} />
          <PrimaryButton width={150} label="Create key" disabled={isSubmitting || !server} onClick={handleSubmit} />
        </div>
      </div>
    </Drawer>
  );
};
